import React, { useState } from "react";
import { Play, X, Film } from "lucide-react";

// Converte URL do YouTube para formato embed
const toYouTubeEmbed = (url) => {
    if (!url) return null;
    // youtu.be/XXX OR youtube.com/watch?v=XXX OR youtube.com/shorts/XXX
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
    return null;
};

// Converte URL do Instagram para embed
const toInstagramEmbed = (url) => {
    if (!url) return null;
    const m = url.match(/instagram\.com\/(p|reel|tv)\/([\w-]+)/);
    if (m) return `https://www.instagram.com/${m[1]}/${m[2]}/embed`;
    return null;
};

// Pega thumbnail do YouTube
const getYouTubeThumb = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
    if (m) return `https://i.ytimg.com/vi/${m[1]}/hqdefault.jpg`;
    return null;
};

const PromoVideo = ({ settings }) => {
    const [open, setOpen] = useState(false);

    if (!settings?.promo_video_enabled || !settings?.promo_video_url) return null;

    const type = settings.promo_video_type || "youtube";
    const url = settings.promo_video_url;
    let embedUrl = null;
    let thumb = settings.promo_video_thumbnail || null;

    if (type === "youtube") {
        embedUrl = toYouTubeEmbed(url);
        if (!thumb) thumb = getYouTubeThumb(url);
    } else if (type === "instagram") {
        embedUrl = toInstagramEmbed(url);
    } else {
        // upload | url — vídeo direto
        embedUrl = url;
    }

    if (!embedUrl) return null;

    const isDirectVideo = type === "upload" || type === "url";

    return (
        <section data-testid="promo-video-section" className="bg-brand-bg py-16 md:py-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-10 max-w-2xl mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-2 inline-flex items-center gap-2">
                        <Film className="w-3.5 h-3.5" /> Vídeo
                    </div>
                    <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-brand-ink tracking-tight">
                        {settings.promo_video_title || "Conheça nossa coleção"}
                    </h2>
                    {settings.promo_video_subtitle && (
                        <p className="mt-3 text-brand-inkSoft text-sm sm:text-base">{settings.promo_video_subtitle}</p>
                    )}
                </div>

                <button
                    onClick={() => setOpen(true)}
                    data-testid="promo-video-trigger"
                    className="group relative block w-full aspect-video bg-brand-ink rounded-md overflow-hidden shadow-2xl ring-1 ring-brand-border hover:ring-brand-gold transition-all"
                >
                    {thumb ? (
                        <img src={thumb} alt={settings.promo_video_title || "Vídeo"} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-brand-dark to-black" />
                    )}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-brand-gold/95 shadow-2xl group-hover:scale-110 transition-transform">
                            <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white fill-white ml-1" strokeWidth={1.5} />
                        </span>
                    </div>
                    <div className="absolute bottom-4 left-4 text-white text-xs uppercase tracking-[0.2em] font-bold bg-black/40 backdrop-blur px-3 py-1.5 rounded-sm">
                        Assistir
                    </div>
                </button>
            </div>

            {open && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setOpen(false)} data-testid="promo-video-modal">
                    <button onClick={() => setOpen(false)} className="absolute top-4 right-4 w-12 h-12 inline-flex items-center justify-center text-white hover:bg-white/10 rounded-full" data-testid="promo-video-close">
                        <X className="w-6 h-6" />
                    </button>
                    <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-5xl aspect-video bg-black rounded-md overflow-hidden shadow-2xl">
                        {isDirectVideo ? (
                            <video src={embedUrl} controls autoPlay className="w-full h-full object-contain bg-black" data-testid="promo-video-player" />
                        ) : (
                            <iframe
                                src={embedUrl}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={settings.promo_video_title || "Vídeo"}
                                data-testid="promo-video-player"
                            />
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default PromoVideo;
